import { useState } from 'react';
import axios from 'axios';

import { uploadStoredFile } from '~/components/Documents/UploadDocumentsCard/utils';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import { reportAxiosError } from '~/Utils';

import type { AnalyzeResponse, PerformOcrResponse, SaveConfigurationRequest, SopConfigurationResponse } from '../types';

interface UseSopConfigurationOptions {
  enabled?: boolean;
  subOrganizationId?: number;
}

export const useSopConfiguration = ({ enabled = true, subOrganizationId }: UseSopConfigurationOptions = {}) => {
  // @ts-expect-error TODO: useSysconfig hook needs TypeScript definition
  const { organization } = useSysconfig();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const urlPrefix = `/sysconfig/api/v1/organizations/${organization.id}/sop`;
  const subOrgParam = subOrganizationId ? `?sub_organization_id=${subOrganizationId}` : '';

  const {
    isLoading,
    isError,
    data: configuration,
    reloadData,
  } = useDataFetcher<SopConfigurationResponse | null>(`${urlPrefix}/configuration${subOrgParam}`, null, {
    shouldFetch: enabled,
  });

  const uploadFile = async (file: File, onProgress: (e: ProgressEvent) => void) => {
    // Step 1: Upload to GCS using existing utility
    const storage_filename = await uploadStoredFile({
      urlPrefix: `/api/v1/organizations/${organization.id}/stored_files`,
      file,
      onUpdatePercent: onProgress,
    });

    if (!storage_filename) {
      throw new Error('Failed to upload file');
    }

    // Step 2: Register in CMS DB using dedicated SOP upload endpoint
    const { data } = await axios.post<{ stored_file_id: number; storage_filename: string }>(
      `${urlPrefix}/upload_finished_callback`,
      {
        storage_filename,
        file_type: file.type,
        file_name: file.name,
        last_modified: new Date().toISOString(),
        file_size: file.size,
      }
    );

    return { stored_file_id: data.stored_file_id, storage_filename: data.storage_filename };
  };

  const performOcr = async (stored_file_id: number): Promise<PerformOcrResponse> => {
    const { data } = await axios.post<PerformOcrResponse>(`${urlPrefix}/perform_ocr`, {
      stored_file_id,
    });
    return data;
  };

  const analyze = async (markdown_pages: string[]): Promise<AnalyzeResponse> => {
    const { data } = await axios.post<AnalyzeResponse>(`${urlPrefix}/analyze`, {
      markdown_pages,
    });
    return data;
  };

  const saveConfiguration = async (config: SaveConfigurationRequest) => {
    try {
      setIsSubmitting(true);
      const requestBody = subOrganizationId ? { ...config, sub_organization_id: String(subOrganizationId) } : config;
      await axios.post(`${urlPrefix}/save_configuration`, requestBody);
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteConfiguration = async () => {
    try {
      setIsSubmitting(true);
      await axios.delete(`${urlPrefix}/configuration${subOrgParam}`);
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isLoading,
    isError,
    configuration,
    isSubmitting,
    hasConfiguration: !!configuration,
    uploadFile,
    performOcr,
    analyze,
    saveConfiguration,
    deleteConfiguration,
    reloadData,
  };
};
